/**
 * Empty State Component
 * Displays placeholder content when no data is available
 */

import React from 'react';
import { FileSpreadsheet, GitBranch, Settings, AlertTriangle, BarChart3 } from 'lucide-react';

type EmptyStateType = 'grid' | 'versions' | 'assumptions' | 'compare' | 'health';

interface EmptyStateProps {
  type: EmptyStateType;
  title?: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

const emptyStateConfig: Record<EmptyStateType, { icon: React.ElementType; title: string; description: string }> = {
  grid: {
    icon: FileSpreadsheet,
    title: 'No Forecast Data',
    description: 'Select a location and year to view forecast data.',
  },
  versions: {
    icon: GitBranch,
    title: 'No Versions Yet',
    description: 'Create your first forecast version to get started.',
  },
  assumptions: {
    icon: Settings,
    title: 'No Assumptions Configured',
    description: 'Add assumptions to drive your forecast calculations.',
  },
  compare: {
    icon: BarChart3,
    title: 'Select Versions to Compare',
    description: 'Choose two versions to see variance analysis.',
  },
  health: {
    icon: AlertTriangle,
    title: 'Health Check Unavailable',
    description: 'Health diagnostics will appear once forecast data is loaded.',
  },
};

export function EmptyState({ type, title, description, action }: EmptyStateProps) {
  const config = emptyStateConfig[type];
  const Icon = config.icon;

  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="text-center max-w-sm">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ background: 'var(--lodge-onyx-100)' }}>
          <Icon className="w-8 h-8" style={{ color: 'var(--lodge-onyx-400)' } as React.CSSProperties} />
        </div>
        <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--lodge-onyx-800)', fontFamily: 'var(--font-sans)', letterSpacing: '-0.2px' }}>
          {title || config.title}
        </h3>
        <p className="text-sm mb-6" style={{ color: 'var(--lodge-onyx-500)' }}>
          {description || config.description}
        </p>
        {action && (
          <button
            onClick={action.onClick}
            className="px-4 py-2 font-semibold rounded-lg transition-all"
            style={{ background: 'var(--lodge-orange-400)', color: '#ffffff', border: 'none', boxShadow: 'var(--shadow-orange)' }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-500)'; (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-400)'; (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)'; }}
          >
            {action.label}
          </button>
        )}
      </div>
    </div>
  );
}

export default EmptyState;
